import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import React, { useState } from "react";
import { Form } from "react-final-form";
import { Disableable } from "..";
import { Text } from "../../../common/components/input/Text";
import { requiredString } from "../../util/validation";
import { CloseableDialogTitle } from "./CloseableDialogTitle";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { Checkbox } from "../../../common/components/input/Checkbox";
import { DpoValues } from "./EditDpoDialog";

type EditPipedaPrivacyOfficerProps = {
  open: boolean;
  onClose: () => void;
  onDone: (vals: PipedaOfficerValues) => void;
  values: PipedaOfficerValues;
  organization: OrganizationDto;
  dpo?: DpoValues;
};

export type PipedaOfficerValues = {
  name: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  address: string | undefined;
};

export const EditPipedaPrivacyOfficerDialog: React.FC<EditPipedaPrivacyOfficerProps> = ({
  open,
  onClose,
  onDone,
  values,
  organization,
  dpo,
}) => {
  const [lock, setLock] = useState(false);

  const handleSubmit = (vals: PipedaOfficerValues) => {
    setLock(false);
    onClose();
    onDone(vals);
  };

  const validate = (values: PipedaOfficerValues) => {
    const errors = {} as { [x: string]: string };
    requiredString(values, "name", errors);
    requiredString(values, "email", errors);
    return errors;
  };

  return (
    <Dialog fullWidth maxWidth={"sm"} open={open}>
      <Form
        onSubmit={handleSubmit}
        validate={validate}
        initialValues={values}
        render={({ handleSubmit, valid, form }) => {
          return (
            <form onSubmit={handleSubmit}>
              <CloseableDialogTitle
                disabledTooltip="Enter Privacy Officer details to proceed"
                onClose={onClose}
              >
                Privacy Officer
              </CloseableDialogTitle>

              <DialogContent className="pt-md">
                {organization.featureGdpr && (
                  <Checkbox
                    field="_ignore"
                    disabled={!dpo}
                    checked={lock}
                    disabledTooltip="You have not added a DPO"
                    label="Use DPO contact information."
                    onChange={(event) => {
                      if (event.target.checked) {
                        setLock(true);
                        form.change("name", dpo.name);
                        form.change("email", dpo.email);
                        form.change("phone", dpo.phone);
                        form.change("address", dpo.address);
                      } else {
                        setLock(false);
                      }
                    }}
                  />
                )}

                <div className="flex-col">
                  <Text
                    multiline={false}
                    variant="outlined"
                    field="name"
                    required
                    autoFocus
                    label="Name or Title"
                    disabled={lock}
                  />
                  <Text
                    multiline={false}
                    variant="outlined"
                    field="email"
                    type="email"
                    required
                    label="Email Address"
                    disabled={lock}
                  />
                  <Text
                    multiline={false}
                    variant="outlined"
                    field="phone"
                    label="Telephone number (optional)"
                    required
                    disabled={lock}
                  />
                  <Text
                    multiline={false}
                    variant="outlined"
                    field="address"
                    required
                    label="Physical address (optional)"
                    disabled={lock}
                  />
                </div>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={() => {
                    setLock(false);
                    onClose();
                  }}
                  color="secondary"
                  className="mr-sm"
                >
                  Cancel
                </Button>

                <Disableable
                  disabledTooltip="Please enter your DPO details to proceed."
                  disabled={!valid}
                >
                  <Button type="submit" color="primary" disabled={!valid} variant="contained">
                    Update
                  </Button>
                </Disableable>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};
